import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { registry, type RegAsset } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { Select, SearchInput } from "@/components/inputs";
import { ago, dateTime } from "@/lib/format";
import { kindLabel, useTemplates } from "@/modules/asset_catalog/common";
import { AssetState, CustodySection, TransferAsset, DriverSection, SlotRow, MembersSection } from "@/modules/registry/AssetsInventory";
import { CompanySelect, RQ, useCompanies, useCompanyScope } from "@/modules/registry/parts_common";

/** Pair & repair — ONE asset, full width: what is fitted (install / remove),
 *  which company owns it, and which driver holds it. Single-subject page
 *  (picker + inline sections, the Edit Driver grammar); the Inventory list's
 *  "Open →" button deep-links here with ?id=. This work needs room — it used
 *  to live in a drawer and every row wrapped. */
export function RegistryPairRepairPage() {
  const qc = useQueryClient();
  const tpl = useTemplates().data;
  const [params, setParams] = useSearchParams();
  const sel = params.get("id");
  const allCompanies = useCompanies().data?.companies ?? [];

  const detail = useQuery({ queryKey: [...RQ, "asset", sel], queryFn: () => registry.asset(sel!), enabled: !!sel });
  const refresh = () => qc.invalidateQueries({ queryKey: [...RQ] });
  const d = detail.data;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Registry · Pair & repair" title={sel ? "Pair & repair" : "Pick an asset"}
        desc="Everything you do to ONE asset: fit or remove subcomponents, move it between companies, and pair it with the driver who holds it." />
      {!sel ? <AssetPicker onPick={(id) => setParams({ id })} />
        : detail.isLoading ? <div className="panel panel-pad cap">Loading…</div>
        : !d ? <div className="panel panel-pad" style={{ color: "var(--bad)", fontSize: 13 }}>
            Asset <span className="mono">{sel}</span> not found.{" "}
            <button className="btn ghost sm" onClick={() => setParams({})}>Pick another</button>
          </div>
        : (<>
          <div className="panel panel-pad" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
            <span className="mono" style={{ fontWeight: 700, fontSize: 16 }}>{d.asset_id}</span>
            <span className="cap">
              {kindLabel(tpl, d.asset_type)} · {d.model ?? "no model"}
            </span>
            <button className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={() => setParams({})}>
              ⇄ Switch asset
            </button>
          </div>

          <div className="section-label">Identity</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <KV k="Model" v={d.model ?? "—"} mono={false} />
            <KV k="Label" v={d.label ?? "—"} mono={false} />
            <KV k="Company" v={d.tenant ?? "In stock (Aphelion)"} mono={false} />
            <KV k="Enrolled" v={dateTime(d.created_at)} />
          </div>

          <div className="section-label">Bill of materials — installed vs catalog</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            {d.slots.map((s) => <SlotRow key={s.slot} asset={d} slot={s} onChanged={refresh} />)}
          </div>

          <div className="section-label">Company</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <TransferAsset asset={d} companies={allCompanies} onChanged={refresh} bare />
          </div>

          {/* Custody: who HAS it, and what is on its way. The door that had no
              console at all — /ship existed and nothing could call it. */}
          <div className="section-label">Custody</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <CustodySection asset={d} companies={allCompanies} onChanged={refresh} bare />
          </div>

          {/* The third write door (§10). Beside Transfer, not inside it: moving
              an asset to `repair` is not the same act as selling it to the
              repairer, and the old one-column model made those look identical. */}
          <div className="section-label">Lifecycle state</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <AssetState asset={d} onChanged={refresh} bare />
          </div>

          {/* D-75: a vehicle is shared by a shift ROSTER (many members, one
              key-holder). MembersSection changes the roster; DriverSection
              hands over the key. Two different facts, two different controls —
              adding a driver must never disturb whoever is riding. */}
          <div className="section-label">Drivers — who MAY drive this vehicle</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <MembersSection asset={d} onChanged={refresh} bare />
          </div>

          <div className="section-label">Key holder — who HAS it right now</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <DriverSection asset={d} onChanged={refresh} bare />
          </div>

          {d.history.length > 0 && (<>
            <div className="section-label">Installation history</div>
            <div className="panel panel-pad">
              {d.history.map((h, n) => (
                <KV key={n} k={h.slot} accent={h.valid_to ? "var(--t-mute)" : "var(--ok)"}
                  v={`${dateTime(h.valid_from)} → ${h.valid_to ? dateTime(h.valid_to) + (h.reason ? ` (${h.reason})` : "") : "now"}${h.actor ? ` · ${h.actor}` : ""}`} />
              ))}
            </div>
          </>)}
        </>)}
    </div>
  );
}

/** Same filters as Inventory → Assets, but picking lands you on the work page. */
function AssetPicker({ onPick }: { onPick: (id: string) => void }) {
  const tpl = useTemplates().data;
  const { companies, scoped } = useCompanyScope();
  const [type, setType] = useState("");
  const [company, setCompany] = useState("");
  const [search, setSearch] = useState("");
  const q = useQuery({
    queryKey: [...RQ, "assets", "picker", type, company, search],
    queryFn: () => registry.assets({ asset_type: type || undefined, company: company || undefined,
      search: search || undefined, limit: "500" }),
  });
  const cols = useMemo<DataCol<RegAsset>[]>(() => [
    { key: "id", label: "Asset ID", width: 180, mono: true, render: (a) => <span style={{ fontWeight: 600 }}>{a.asset_id}</span> },
    { key: "type", label: "Type", width: 90, render: (a) => kindLabel(tpl, a.asset_type) },
    { key: "model", label: "Model", width: 200, render: (a) => a.model ?? <span style={{ color: "var(--bad)" }}>no model</span> },
    { key: "label", label: "Label", width: 150, render: (a) => a.label ?? "—" },
    { key: "company", label: "Company", width: 160, render: (a) => a.tenant ?? <span className="muted">In stock</span> },
    { key: "parts", label: "Parts", width: 70, mono: true, render: (a) => a.parts_installed },
    { key: "created", label: "Enrolled", width: 90, mono: true, render: (a) => ago(a.created_at), title: (a) => dateTime(a.created_at) },
  ], [tpl]);
  return (<>
    <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
      <SearchInput placeholder="Search asset id / label…" value={search}
        onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
      <Select value={type} onChange={(e) => setType(e.target.value)}>
        <option value="">All types</option><option value="vehicle">Vehicle</option><option value="battery">Battery</option>
      </Select>
      <CompanySelect value={company} onChange={setCompany} companies={companies} scoped={scoped} />
    </div>
    <DataTable rows={q.data?.assets ?? []} cols={cols} rowKey={(r) => r.asset_id} pageSize={100}
      onRowClick={(r) => onPick(r.asset_id)}
      empty={<div className="panel panel-pad cap">
        No assets — enrol one on the Enroll page (What = Asset).
      </div>} />
  </>);
}
